import { AsyncErrorBoundary } from '@/context/async-error-boundary';
import { useAuthContext } from '@/context/auth-provider';
import { toast } from '@/hooks/use-toast';
import { updateUserProfileQueryFn } from '@/lib/api';
import { useZodForm } from '@/lib/zod';
import { UserDataSchema } from '@/schemas';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { LoaderCircleIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '../ui/form';
import { Input } from '../ui/input';

export default function UserData() {
    const { user } = useAuthContext();
    const userDataForm = useZodForm(UserDataSchema, {
        defaultValues: {
            name: user.name,
        },
        mode: 'onChange',
    });

    const queryClient = useQueryClient();
    const { mutate, isPending } = useMutation({
        mutationFn: updateUserProfileQueryFn,
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: ['authUser'],
                refetchType: 'all',
            });
        },
    });

    function handleProfileUpdate(values: { name: string }) {
        if (!values.name) {
            return;
        }

        mutate(
            { name: values.name, userId: user.id },
            {
                onSuccess: (response) => {
                    toast({
                        title: 'Success',
                        description: response.data.message,
                    });
                },
                onError: (error) => {
                    toast({
                        title: 'Error',
                        description: error.message,
                        variant: 'destructive',
                    });
                },
            },
        );
    }

    return (
        <>
            <div className='max-w-sm flex flex-col py-6 px-2 flex-1 text-sm text-muted-foreground self-start'>
                <span className='font-bold text-lg text-foreground'>
                    Full Name
                </span>
                <p className='mb-4'>Your full name can be changed here.</p>
            </div>
            <AsyncErrorBoundary>
                <Card className='flex-1 w-full border-none bg-accent/50'>
                    <CardContent className='space-y-4 p-2 md:p-6'>
                        <Form {...userDataForm}>
                            <form
                                noValidate
                                onSubmit={userDataForm.handleSubmit(
                                    handleProfileUpdate,
                                )}>
                                <FormField
                                    control={userDataForm.control}
                                    name='name'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Full Name</FormLabel>
                                            <FormControl>
                                                <Input
                                                    {...field}
                                                    placeholder='John Doe'
                                                    disabled={isPending}
                                                />
                                            </FormControl>
                                            <FormDescription>
                                                Enter your full name, i.e: John
                                                Doe.
                                            </FormDescription>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <div className='flex items-center gap-4'>
                                    <Button
                                        className='my-4 w-full sm:w-auto'
                                        disabled={
                                            isPending ||
                                            userDataForm.control.getFieldState(
                                                'name',
                                            ).invalid ||
                                            userDataForm.watch().name.trim() ===
                                                user.name ||
                                            (!userDataForm.watch().name &&
                                                !user.name)
                                        }>
                                        {isPending ? (
                                            <>
                                                <LoaderCircleIcon
                                                    size={5}
                                                    className='animate-spin'
                                                />
                                                Saving...
                                            </>
                                        ) : (
                                            'Save'
                                        )}
                                    </Button>
                                </div>
                            </form>
                        </Form>
                    </CardContent>
                </Card>
            </AsyncErrorBoundary>
        </>
    );
}
